import { useTranslation } from 'react-i18next';
import { parsePriceInput } from '~/utils/moneyBounds';
import { PricePerPieceTooltip } from '~/components/ui';
import type { UnitCode } from '~/graphql/graphql';
import { priceUnitLabel } from '~/utils';
import { SuggestedPriceInput } from './SuggestedPriceInput';

type CartItemSuggestPricePopoverProps = {
  sellerDisplayPricePerUnit: number | null;
  pricePerPiece: number | null;
  draftPrice: string;
  draftPricePerPiece: number | null;
  priceUnit: UnitCode;
  currency?: string;
  onDraftPriceChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export function CartItemSuggestPricePopover({
  sellerDisplayPricePerUnit,
  pricePerPiece,
  draftPrice,
  draftPricePerPiece,
  priceUnit,
  currency,
  onDraftPriceChange,
  onConfirm,
  onCancel,
}: Readonly<CartItemSuggestPricePopoverProps>) {
  const { t } = useTranslation('cart');

  return (
    <div
      data-testid="suggest-price-popover"
      className="absolute right-0 top-0 mt-7 z-20 bg-neut-0 border border-neut-border rounded-xl shadow-lg p-3 w-56 text-left"
    >
      <div className="mb-2">
        <SuggestedPriceInput
          label={t('cart.suggest_price_title')}
          value={draftPrice}
          onChange={onDraftPriceChange}
          testId="suggest-price-input"
          currency={currency}
          unitLabel={priceUnitLabel(priceUnit)}
          showErrorWhileTyping
          placeholder={
            sellerDisplayPricePerUnit != null
              ? sellerDisplayPricePerUnit.toFixed(2)
              : undefined
          }
          hint={
            sellerDisplayPricePerUnit != null && (
              <span className="flex items-center gap-1">
                {t('cart.suggest_price_original', {
                  price: sellerDisplayPricePerUnit.toFixed(2),
                  unit: priceUnitLabel(priceUnit),
                })}
                {priceUnit === 'PCS' && (
                  <PricePerPieceTooltip
                    pricePerPiece={sellerDisplayPricePerUnit}
                    currency={currency}
                    iconOnly
                    className="text-neut-sec"
                  />
                )}
              </span>
            )
          }
        />
      </div>
      {sellerDisplayPricePerUnit != null &&
        pricePerPiece != null &&
        priceUnit !== 'PCS' && (
          <div className="flex items-center gap-1 mb-2">
            <span className="text-xs text-neut-sec">
              {t('cart.suggest_price_original_per_piece')}
            </span>
            <PricePerPieceTooltip
              pricePerPiece={pricePerPiece}
              currency={currency}
              className="text-xs font-semibold text-neut-sec"
            />
          </div>
        )}
      {draftPricePerPiece != null && (
        <div
          data-testid="suggest-price-draft-per-piece"
          className="flex items-center gap-1 mb-2"
        >
          <span className="text-xs text-neut-sec">
            {t('cart.suggest_price_new_per_piece')}
          </span>
          <PricePerPieceTooltip
            pricePerPiece={draftPricePerPiece}
            currency={currency}
            className="text-xs font-semibold text-neut-sec"
          />
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={parsePriceInput(draftPrice).amount == null}
          className="flex-1 text-xs px-2 py-1.5 bg-prim text-neut-0 rounded-lg hover:bg-prim/90 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors"
          onClick={onConfirm}
        >
          {t('cart.suggest_price_confirm')}
        </button>
        <button
          type="button"
          className="text-xs px-2 py-1.5 border border-neut-border text-neut-sec rounded-lg hover:bg-neut-hover transition-colors"
          onClick={onCancel}
        >
          {t('cart.suggest_price_cancel')}
        </button>
      </div>
    </div>
  );
}
